import type { Accent, ImportRow, LearningTrack, Rating, RichImportPayload, Skill } from "./types";

export const IMPORT_FIELDS = [
  "term", "part_of_speech", "ipa", "meaning_vi", "definition_en",
  "example1_en", "translation1", "example2_en", "translation2",
] as const;

export function normalizeTerm(value: unknown): string {
  return String(value ?? "").normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase("en");
}

export function normalizeAnswer(value: unknown): string {
  return String(value ?? "").trim().toLocaleLowerCase("en").replace(/[\s\u00a0]+/g, " ").replace(/^[\s.,!?;:]+|[\s.,!?;:]+$/g, "");
}

export function chooseAccent<T>(audio: Record<Accent, T | null>, requested: Accent): T | null {
  return audio[requested] ?? audio[requested === "us" ? "uk" : "us"];
}

export function skillLearningTrack(skill: Skill): LearningTrack {
  if (["dictation", "audio_word_choice", "audio_meaning_choice"].includes(skill)) return "listening";
  if (["production", "spelling_correction", "missing_letters"].includes(skill)) return "context";
  return "meaning";
}

export const learningTrackLabels: Record<LearningTrack, string> = {
  meaning: "Nhớ nghĩa",
  listening: "Nghe hiểu",
  context: "Ngữ cảnh",
};

export function streakStats(dateValues: string[]) {
  const dates = [...new Set(dateValues)].sort();
  let best = 0; let run = 0; let previous: Date | null = null;
  for (const value of dates) {
    const current = new Date(`${value}T00:00:00Z`);
    run = previous && current.getTime() - previous.getTime() === 86_400_000 ? run + 1 : 1;
    best = Math.max(best, run); previous = current;
  }
  let current = 0;
  if (dates.length) {
    let cursor = new Date(`${dates.at(-1)}T00:00:00Z`);
    for (let index = dates.length - 1; index >= 0; index--) {
      if (dates[index] !== cursor.toISOString().slice(0, 10)) break;
      current++; cursor = new Date(cursor.getTime() - 86_400_000);
    }
  }
  return { current, best, last: dates.at(-1) ?? null };
}

export function validateImportValues(values: string[], rowNumber = 0): string[] {
  const errors: string[] = [];
  if (values.length !== IMPORT_FIELDS.length) return [`Dòng ${rowNumber || "?"}: cần đúng 9 trường, hiện có ${values.length}.`];
  const [term, part, ipa, meaning, definition, ex1, tr1, ex2, tr2] = values.map((value) => value.trim());
  if (!term) errors.push("Thiếu từ.");
  if (!part) errors.push("Thiếu từ loại.");
  if (!ipa) errors.push("Thiếu IPA.");
  if (!meaning) errors.push("Thiếu nghĩa tiếng Việt.");
  if (!definition) errors.push("Thiếu định nghĩa tiếng Anh.");
  if (!ex1 || !tr1 || !ex2 || !tr2) errors.push("Cần đủ hai ví dụ và hai bản dịch.");
  if (/[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i.test(ex1 + ex2)) {
    errors.push("Ví dụ tiếng Anh có vẻ chứa tiếng Việt.");
  }
  return errors;
}

function asText(value: unknown): string {
  return String(value ?? "").trim();
}

function validateRichPayload(payload: RichImportPayload, rowNumber = 0): string[] {
  const errors = validateImportValues([
    payload.term,
    payload.part_of_speech,
    payload.ipa,
    payload.meaning_vi,
    payload.definition_en,
    payload.examples[0]?.sentence_en ?? "",
    payload.examples[0]?.translation_vi ?? "",
    payload.examples[1]?.sentence_en ?? "",
    payload.examples[1]?.translation_vi ?? "",
  ], rowNumber);
  if (!payload.definition_vi?.trim()) errors.push("Thiếu giải nghĩa tiếng Việt.");
  if (!payload.cambridge_url?.trim()) errors.push("Thiếu URL Cambridge hoặc trạng thái chưa xác minh.");
  if (payload.verification_status !== "verified" && payload.verification_status !== "unverified") errors.push("verification_status phải là verified hoặc unverified.");
  if (payload.verification_status === "verified" && !/^https:\/\/dictionary\.cambridge\.org\//.test(payload.cambridge_url ?? "")) errors.push("Nguồn đã xác minh cần là URL Cambridge.");
  if (payload.examples.length < 4) errors.push("JSON v2 cần ít nhất 4 ví dụ/ngữ cảnh.");
  return errors;
}

function richValues(payload: RichImportPayload) {
  return [
    payload.term,
    payload.part_of_speech,
    payload.ipa,
    payload.meaning_vi,
    payload.definition_en,
    payload.examples[0]?.sentence_en ?? "",
    payload.examples[0]?.translation_vi ?? "",
    payload.examples[1]?.sentence_en ?? "",
    payload.examples[1]?.translation_vi ?? "",
  ];
}

function normalizeRichImportItem(value: unknown): RichImportPayload {
  const row = (value && typeof value === "object" ? value : {}) as Record<string, unknown>;
  const examples = Array.isArray(row.examples) ? row.examples : [];
  return {
    id: asText(row.id) || undefined,
    term: asText(row.term),
    part_of_speech: asText(row.part_of_speech),
    ipa: asText(row.ipa),
    meaning_vi: asText(row.meaning_vi),
    definition_en: asText(row.definition_en),
    definition_vi: asText(row.definition_vi),
    cambridge_url: asText(row.cambridge_url),
    cambridge_sense: asText(row.cambridge_sense),
    verification_status: asText(row.verification_status || "unverified") || "unverified",
    collocations: Array.isArray(row.collocations) ? row.collocations.map(asText).filter(Boolean) : [],
    distractors: Array.isArray(row.distractors) ? row.distractors.map((distractor) => {
      const record = (distractor && typeof distractor === "object" ? distractor : {}) as Record<string, unknown>;
      return { term: asText(record.term), meaning_vi: asText(record.meaning_vi), explanation: asText(record.explanation) };
    }).filter((distractor) => distractor.term || distractor.meaning_vi) : [],
    examples: examples.map((example) => {
      const record = (example && typeof example === "object" ? example : {}) as Record<string, unknown>;
      const accepted = Array.isArray(record.accepted_answers) ? record.accepted_answers.map(asText).filter(Boolean) : undefined;
      return { sentence_en: asText(record.sentence_en), translation_vi: asText(record.translation_vi), accepted_answers: accepted };
    }),
  };
}

function parseRichImportText(text: string) {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  const root = parsed && typeof parsed === "object" ? parsed as Record<string, unknown> : null;
  const items = Array.isArray(root?.items) ? root.items : Array.isArray(parsed) ? parsed : null;
  if (!items) return null;
  const rows: ImportRow[] = items.map((item, index) => {
    const rich = normalizeRichImportItem(item);
    const values = richValues(rich);
    const errors = validateRichPayload(rich, index + 1);
    return {
      row_number: index + 1,
      values,
      fields: {
        id: rich.id ?? "",
        ...Object.fromEntries(IMPORT_FIELDS.map((field, fieldIndex) => [field, values[fieldIndex] ?? ""])),
        definition_vi: rich.definition_vi ?? "",
        cambridge_url: rich.cambridge_url ?? "",
        cambridge_sense: rich.cambridge_sense ?? "",
        verification_status: rich.verification_status ?? "unverified",
        collocations: rich.collocations?.join(", ") ?? "",
        distractors: rich.distractors?.map((distractor) => distractor.term || distractor.meaning_vi).filter(Boolean).join(", ") ?? "",
      },
      rich_payload: rich,
      errors,
      warnings: [],
      status: errors.length ? "error" as const : "ready" as const,
    };
  });
  return { rows, counts: { total: rows.length, ready: rows.filter((row) => row.status !== "error").length, error: rows.filter((row) => row.status === "error").length } };
}

export function parseImportText(text: string) {
  const rich = parseRichImportText(text.trim());
  if (rich) return rich;
  const rows: ImportRow[] = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean).map((line, index) => {
    const values = line.split("|").map((value) => value.trim());
    const errors = validateImportValues(values, index + 1);
    return {
      row_number: index + 1,
      values,
      fields: Object.fromEntries(IMPORT_FIELDS.map((field, fieldIndex) => [field, values[fieldIndex] ?? ""])),
      errors,
      warnings: [],
      status: errors.length ? "error" as const : "ready" as const,
    };
  });
  return { rows, counts: { total: rows.length, ready: rows.filter((row) => row.status !== "error").length, error: rows.filter((row) => row.status === "error").length } };
}

export interface SchedulerInput {
  state: string;
  stability: number;
  difficulty: number;
  reps: number;
  lapses: number;
}

export function scheduleReview(card: SchedulerInput, rating: Rating, now = new Date()) {
  const reps = card.reps + 1;
  const lapses = card.lapses + (rating === 1 ? 1 : 0);
  const adjustment = rating === 2 ? 0.5 : rating === 4 ? -0.5 : 0;
  const difficulty = Math.max(1, Math.min(10, card.difficulty + adjustment));
  if (rating === 1) {
    return { state: "learning", stability: 1, difficulty: Math.min(10, difficulty + 1), reps, lapses, dueAt: new Date(now.getTime() + 10 * 60_000) };
  }
  const multiplier = ({ 2: 1.2, 3: 2.4, 4: 3.8 } as const)[rating];
  const stability = Math.max(1, card.stability ? card.stability * multiplier : multiplier);
  const interval = Math.max(1, Math.min(36_500, Math.round(stability * Math.pow(0.9, Math.max(0, difficulty - 5)))));
  return { state: interval >= 21 ? "review" : "learning", stability, difficulty, reps, lapses, dueAt: new Date(now.getTime() + interval * 86_400_000) };
}
